import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useProBranding } from "@/hooks/use-pro-branding";
import { generateBrandedPdf } from "@/lib/pdfBranded";
import {
  listDocs,
  listChatMessages,
  deleteDoc,
  deleteConversation as removeConversation,
  subscribeDocs,
  subscribeChats,
  type LocalDoc,
  type LocalChatMessage,
} from "@/lib/localHistory";
import { FileText, MessageSquare, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/historico")({
  head: () => ({
    meta: [
      { title: "Meu histórico de PDFs e conversas IA | MEI Toolbox" },
      {
        name: "description",
        content:
          "Acesse o histórico completo dos PDFs gerados nas ferramentas para MEI e das suas conversas com o assistente de IA — tudo salvo com segurança no seu navegador.",
      },
      { property: "og:title", content: "Meu histórico — MEI Toolbox" },
      {
        property: "og:description",
        content: "Histórico de PDFs gerados e conversas com o assistente IA do MEI Toolbox.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/historico" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/historico" }],
  }),
  component: HistoryPage,
});

type ChatRow = {
  conversation_id: string;
  first_message: string;
  last_at: string;
  count: number;
};

function HistoryPage() {
  const { branding } = useProBranding();
  const [tab, setTab] = useState<"docs" | "chats">("docs");
  const [docs, setDocs] = useState<LocalDoc[]>([]);
  const [messages, setMessages] = useState<LocalChatMessage[]>([]);
  const [openConv, setOpenConv] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => {
      setDocs(listDocs());
      setMessages(listChatMessages());
    };
    refresh();
    const u1 = subscribeDocs(refresh);
    const u2 = subscribeChats(refresh);
    return () => {
      u1();
      u2();
    };
  }, []);

  const chats = useMemo<ChatRow[]>(() => {
    const map = new Map<string, ChatRow>();
    messages.forEach((m) => {
      const existing = map.get(m.conversation_id);
      if (!existing) {
        map.set(m.conversation_id, {
          conversation_id: m.conversation_id,
          first_message: m.role === "user" ? m.content : "",
          last_at: m.created_at,
          count: 1,
        });
      } else {
        existing.count += 1;
        if (m.created_at > existing.last_at) existing.last_at = m.created_at;
        if (!existing.first_message && m.role === "user") existing.first_message = m.content;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.last_at.localeCompare(a.last_at));
  }, [messages]);

  const convMsgs = useMemo(
    () =>
      openConv
        ? messages
            .filter((m) => m.conversation_id === openConv)
            .sort((a, b) => a.created_at.localeCompare(b.created_at))
        : [],
    [messages, openConv],
  );

  const reDownload = async (doc: LocalDoc) => {
    try {
      await generateBrandedPdf({ ...doc.payload, branding });
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível gerar o PDF novamente");
    }
  };

  const handleDeleteDoc = (id: string) => {
    if (!confirm("Remover este PDF do histórico?")) return;
    deleteDoc(id);
    toast.success("Removido");
  };

  const handleDeleteConv = (convId: string) => {
    if (!confirm("Remover esta conversa inteira?")) return;
    removeConversation(convId);
    if (openConv === convId) setOpenConv(null);
    toast.success("Conversa removida");
  };

  return (
    <section className="container mx-auto max-w-4xl px-4 py-10">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Meu histórico</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tudo o que você gera fica salvo aqui — PDFs e conversas com a IA. Os dados ficam{" "}
            <strong>somente neste navegador</strong>.
          </p>
        </div>
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground underline">
          ← Início
        </Link>
      </div>

      <div className="mt-6 inline-flex rounded-full border border-border p-1 bg-card">
        <button
          onClick={() => setTab("docs")}
          className={`px-4 py-1.5 text-sm font-semibold rounded-full ${tab === "docs" ? "bg-amber-500 text-white" : "text-muted-foreground"}`}
        >
          <FileText className="inline h-4 w-4 mr-1" /> PDFs ({docs.length})
        </button>
        <button
          onClick={() => setTab("chats")}
          className={`px-4 py-1.5 text-sm font-semibold rounded-full ${tab === "chats" ? "bg-amber-500 text-white" : "text-muted-foreground"}`}
        >
          <MessageSquare className="inline h-4 w-4 mr-1" /> Conversas ({chats.length})
        </button>
      </div>

      {tab === "docs" && (
        <div className="mt-6 space-y-3">
          {docs.length === 0 && (
            <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Nenhum PDF gerado ainda. Use qualquer ferramenta e clique em "Exportar PDF com sua
              marca".
            </p>
          )}
          {docs.map((d) => (
            <div
              key={d.id}
              className="rounded-2xl border border-border bg-card p-4 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="font-bold truncate">{d.title}</p>
                <p className="text-xs text-muted-foreground">
                  {d.tool} · {new Date(d.created_at).toLocaleString("pt-BR")}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => reDownload(d)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 px-3 py-1.5 text-xs font-bold text-white"
                >
                  <Download className="h-3.5 w-3.5" /> Baixar
                </button>
                <button
                  onClick={() => handleDeleteDoc(d.id)}
                  className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-red-600 hover:border-red-300"
                  title="Remover PDF"
                  aria-label="Remover PDF"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "chats" && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {chats.length === 0 && (
              <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                Nenhuma conversa salva ainda.
              </p>
            )}
            {chats.map((c) => (
              <div
                key={c.conversation_id}
                className={`rounded-2xl border p-4 cursor-pointer transition ${openConv === c.conversation_id ? "border-amber-400 bg-amber-50/40 dark:bg-amber-950/10" : "border-border bg-card hover:border-amber-300"}`}
                onClick={() => setOpenConv(c.conversation_id)}
              >
                <p className="font-semibold text-sm truncate">{c.first_message || "(sem texto)"}</p>
                <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {c.count} mensagens · {new Date(c.last_at).toLocaleDateString("pt-BR")}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConv(c.conversation_id);
                    }}
                    className="text-muted-foreground hover:text-red-600"
                    aria-label="Remover conversa"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card p-4 max-h-[70vh] overflow-y-auto space-y-3">
            {!openConv && (
              <p className="text-sm text-muted-foreground text-center py-12">
                Selecione uma conversa para ver o histórico.
              </p>
            )}
            {openConv &&
              convMsgs.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-amber-500 text-white" : "bg-muted"}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <article className="mt-16 prose prose-sm max-w-none dark:prose-invert">
        <h2>Seu histórico de documentos e conversas do MEI Toolbox</h2>
        <p>
          O histórico reúne, em um único lugar, tudo que você gerou usando as ferramentas gratuitas
          do MEI Toolbox: PDFs de contratos, recibos, cálculos de DAS, planilhas de precificação,
          simulações de empréstimo e maquininha, além de todas as conversas que você teve com o
          <Link to="/ferramentas/ia-mei"> assistente de IA</Link>. É a forma mais prática de
          consultar documentos antigos sem precisar repetir o preenchimento do zero.
        </p>

        <h3>Como o histórico funciona</h3>
        <p>
          Cada vez que você clica em "Gerar PDF" em uma das ferramentas, o documento é salvo
          automaticamente aqui. Ao abrir o histórico, basta clicar em "Baixar" para gerar o mesmo
          arquivo novamente — com a<Link to="/marca"> marca da sua empresa</Link> aplicada — sem
          precisar recadastrar nada. Para conversas, cada sessão com a IA fica disponível na aba
          "Conversas", organizada por data e com a primeira pergunta como título, o que facilita
          encontrar respostas antigas.
        </p>

        <h3>Privacidade: tudo fica só no seu navegador</h3>
        <p>
          O MEI Toolbox <strong>não envia esses dados para nenhum servidor</strong>. PDFs e
          mensagens ficam armazenados localmente, usando o storage do próprio navegador. Isso
          significa que limpar os dados de navegação, usar modo anônimo ou trocar de aparelho remove
          o histórico — é uma escolha consciente nossa para preservar a sua privacidade. Para
          guardar documentos importantes a longo prazo, recomendamos baixar o PDF e arquivá-lo em um
          serviço de nuvem da sua confiança.
        </p>

        <h3>Como gerenciar o histórico</h3>
        <ul>
          <li>
            <strong>Baixar de novo:</strong> regera o PDF com a marca atual da sua empresa.
          </li>
          <li>
            <strong>Remover:</strong> apaga o item do histórico (a ação não pode ser desfeita).
          </li>
          <li>
            <strong>Limpar tudo:</strong> use as configurações do navegador para apagar o storage do
            site.
          </li>
        </ul>

        <h3>Dicas para aproveitar melhor</h3>
        <p>
          Configure sua <Link to="/marca">marca</Link> antes de gerar documentos importantes — logo,
          CNPJ, telefone e endereço aparecem em todos os PDFs e dão um acabamento profissional. Se
          você usa o MEI Toolbox em mais de um aparelho (celular e computador, por exemplo),
          lembre-se de que cada dispositivo terá um histórico próprio. E sempre que precisar de
          orientação sobre os documentos, abra uma nova conversa com a IA do MEI a partir do link no
          topo.
        </p>
      </article>
    </section>
  );
}
